import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChatbotInput } from "@/components/ChatbotInput";
import { AppHeader } from "@/components/Layout/AppHeader";
import { useUndoStack } from "@/hooks/useUndoStack";
import { StatusBadge } from "@/components/Layout/StatusBadge";
import { BudgetStatusSheet } from "@/components/Gamification/BudgetStatusSheet";
import { LevelUpModal } from "@/components/Gamification/LevelUpModal";
import { WeeklyReflectionModal } from "@/components/Gamification/WeeklyReflectionModal";
import { BadgeNotification } from "@/components/Gamification/BadgeNotification";
import { getVersionString } from "@/lib/version";
import { InstallBanner } from "@/components/PWA/InstallBanner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGamification } from "@/contexts/GamificationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useBudget } from "@/contexts/BudgetContext";
import { isDevelopment, getTestBadgeIds } from "@/lib/devHelper";
import { Button } from "@/components/ui/button";
import { FlaskConical } from "lucide-react";
import { HomePageSkeleton } from "@/components/Loading/HomePageSkeleton";

const Home = () => {
  const { t } = useLanguage();
  const { settings: gamificationSettings } = useGamification();
  const { user, isLoading } = useAuth();
  const { refresh: refreshBudget } = useBudget();
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState(1);
  const [showReflection, setShowReflection] = useState(false);
  const [newBadges, setNewBadges] = useState<string[]>([]);
  const [budgetSheetOpen, setBudgetSheetOpen] = useState(false);
  const navigate = useNavigate();
  const { pushUndo } = useUndoStack(() => refreshBudget());
  const calmMode = gamificationSettings.hideRewards;

  // Dev-only: test badge modal trigger
  const handleTestBadges = (count: 1 | 2 | 3) => {
    setNewBadges(getTestBadgeIds(count));
  };

  // Redirect to auth or onboarding if needed
  useEffect(() => {
    if (isLoading) return;
    
    const onboardingComplete = localStorage.getItem('onboardingComplete');
    
    if (!user) {
      navigate('/auth');
    } else if (!onboardingComplete) {
      navigate('/onboarding');
    }
  }, [user, isLoading, navigate]);

  const handleRefresh = () => {
    refreshBudget();
  };

  if (isLoading || !user) {
    return <HomePageSkeleton />;
  }

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      <AppHeader
        title={t('home.title')}
        showDrawerTrigger={true}
        rightActions={
          <>
            <StatusBadge onOpenSheet={() => setBudgetSheetOpen(true)} />
          </>
        }
      />

      <main id="main-content" className="flex-1 flex flex-col overflow-hidden pt-20">
        {/* Dev Test Buttons */}
        {isDevelopment && (
          <div className="flex gap-2 items-center bg-amber-100 dark:bg-amber-900/30 p-2 mx-4 rounded-lg border border-amber-300 dark:border-amber-700">
            <FlaskConical className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">Dev:</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleTestBadges(1)}>
              1 Badge
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleTestBadges(2)}>
              2 Badges
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleTestBadges(3)}>
              3 Badges
            </Button>
          </div>
        )}

        {/* Chat Interface - Pure, focused conversation */}
        <div className="flex-1 bg-card flex flex-col overflow-hidden">
          <ChatbotInput 
            onExpenseAdded={handleRefresh} 
            pushUndo={pushUndo} 
            userId={user.id}
            onBadgeUnlocked={(badges) => setNewBadges(badges)}
          />
        </div>
      </main>

      {/* Budget Status Sheet */}
      <BudgetStatusSheet
        open={budgetSheetOpen}
        onOpenChange={setBudgetSheetOpen}
        userId={user.id}
      />

      {/* Gamification Modals */}
      <LevelUpModal 
        open={showLevelUp}
        onClose={() => setShowLevelUp(false)}
        newLevel={newLevel}
        calmMode={calmMode}
      />
      <WeeklyReflectionModal 
        userId={user.id}
        open={showReflection}
        onClose={() => setShowReflection(false)}
      />
      <BadgeNotification 
        badgeIds={newBadges}
        onDismiss={() => setNewBadges([])}
      />

      <InstallBanner />
      
      <footer className="text-xs text-muted-foreground text-center py-2 border-t">
        {getVersionString()}
      </footer>
    </div>
  );
};

export default Home;
